import React, { act, useEffect, useState } from "react";
import {
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    useTheme,
} from "@mui/material";
import {
    SettingsOutlined,
    ChevronLeft,
    ChevronRightOutlined,
    HomeOutlined,
    ShoppingCartOutlined,
    Groups2Outlined,
    ReceiptLongOutlined,
    PublicOutlined,
    PointOfSaleOutlined,
    TodayOutlined,
    CalendarMonthOutlined,
    AdminPanelSettingsOutlined,
    TrendingUpOutlined,
    PieChartOutlined,
    PersonAddOutlined,
    PermIdentityOutlined,
    PermContactCalendarOutlined
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import FlexBetween from "./FlexBetween";
import profileImage from "assets/profile.png";
import { AuthUser } from "state/authSlice";

interface SidebarProps {
    isNonMobile: boolean;
    drawerWidth: string;
    isSidebarOpen: boolean;
    setIsSidebarOpen: (isOpen: boolean) => void;
    user: AuthUser | null;
}

interface NavItem {
    text: string;
    icon: JSX.Element | null;
    relativePath?: string;
    shouldBeAdmin: boolean;
}

const navItems: Array<NavItem> = [
    {
        text: "Admin Dashboard",
        icon: <AdminPanelSettingsOutlined />,
        shouldBeAdmin: true,
        relativePath: "admindashboard",
    },
    {
        text: "Dashboard",
        icon: <HomeOutlined />,
        relativePath: "dashboard",
        shouldBeAdmin: false,
    },
    // { text: "School", icon: null },
    {
        text: "Students",
        icon: <Groups2Outlined />,
        relativePath: "students",
        shouldBeAdmin: false,
    },
    {
        text: "Book Appointment",
        icon: <PermContactCalendarOutlined />,
        relativePath: "professionals",
        shouldBeAdmin: false,
    },
    // { text: "Add User", icon: <PersonAddOutlined />, relativePath: "adduser" },
    // { text: "Profile", icon: <PermIdentityOutlined />, relativePath: "profile" },
    // { text: "Geography", icon: <PublicOutlined /> },
    // { text: "Sales", icon: null },
    // { text: "Overview", icon: <PointOfSaleOutlined /> },
    // { text: "Daily", icon: <TodayOutlined /> },
    // { text: "Monthly", icon: <CalendarMonthOutlined /> },
    // { text: "Breakdown", icon: <PieChartOutlined /> },
    // { text: "Management", icon: null },
    // { text: "Performance", icon: <TrendingUpOutlined /> },
];

const Sidebar = ({
    drawerWidth,
    isSidebarOpen,
    setIsSidebarOpen,
    isNonMobile,
    user,
}: SidebarProps) => {
    const { pathname } = useLocation();
    const [active, setActive] = useState("");
    const navigate = useNavigate();
    const theme = useTheme();

    useEffect(() => {
        setActive(pathname.substring(1));
    }, [pathname]);

    return (
        <Box component="nav" sx={{ border: `1px solid ${theme.palette.secondary[200]}` }} >
            {isSidebarOpen && (
                <Drawer
                    open={isSidebarOpen}
                    onClose={() => setIsSidebarOpen(false)}
                    PaperProps={{
                        sx: {
                            borderRight: isSidebarOpen ? `1px solid ${theme.palette.secondary[200]}` : 'none',
                            transition: 'border 0.3s',
                        },
                    }}
                    variant="persistent"
                    anchor="left"
                    sx={{
                        width: drawerWidth,
                        "& .MuiDrawer-paper": {
                            color: theme.palette.secondary[200],
                            marginTop: "75px", // Adjust for the height of the Navbar
                            boxSizing: "border-box",
                            width: drawerWidth,
                        },
                    }}
                >
                    <Box width="100%">
                        <Box>
                            <FlexBetween color={theme.palette.secondary.main}>

                                {!isNonMobile && (
                                    <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                                        <ChevronLeft />
                                    </IconButton>
                                )}
                            </FlexBetween>
                        </Box>
                        <List>
                            {navItems.map((item, index) => {
                                if (
                                    !item.shouldBeAdmin ||
                                    (item.shouldBeAdmin && user?.isAdmin)
                                ) {
                                    if (!item.icon) {
                                        return (
                                            <Typography
                                                key={item.text}
                                                sx={{ margin: "2rem 0 1rem 2rem" }}
                                            >
                                                {item.text.toLowerCase() === 'dashboard' && user?.isAdmin ? `School ${item.text}` : item.text}
                                            </Typography>
                                        );
                                    }
                                    // const lcText = item.text.toLowerCase();
                                    const lcText =
                                        item.relativePath?.toLocaleLowerCase() ||
                                        item.text.toLowerCase();
                                    return (
                                        <ListItem key={item.text} disablePadding>
                                            <ListItemButton
                                                onClick={() => {
                                                    navigate(`/${lcText}`);
                                                    setActive(lcText);
                                                }}
                                                sx={{
                                                    backgroundColor:
                                                        active === lcText
                                                            ? theme.palette.secondary[300]
                                                            : "transparent",
                                                    color:
                                                        active === lcText
                                                            ? theme.palette.primary[600]
                                                            : theme.palette.secondary[200],
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        ml: "0.5rem",
                                                        color:
                                                            active === lcText
                                                                ? theme.palette.primary[600]
                                                                : theme.palette.secondary[200],
                                                    }}
                                                >
                                                    {item.icon}
                                                </ListItemIcon>
                                                <ListItemText primary={item.text.toLowerCase() === 'dashboard' && user?.isAdmin ? `School ${item.text}` : item.text} />
                                                {active === lcText && (
                                                    <ChevronRightOutlined sx={{ ml: "auto" }} />
                                                )}
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                }
                            })}
                        </List>
                    </Box>

                </Drawer>
            )}
        </Box>
    );
};

export default Sidebar;
